import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader, X, AlertTriangle, ExternalLink } from 'lucide-react';
import {
  expensesApi,
  ExpenseCreatePayload,
  ExpenseDraft,
  ExpenseDTO,
} from '../../api/expenses';
import { CATEGORIES, POPULAR_CURRENCIES } from '../../constants/expenses';
import { useToast } from '../ui/Toast';
import { errorMessage } from '../../utils/errors';

/**
 * Editable confirmation card for an expense the assistant parsed out of chat.
 *
 * Nothing reaches the database until the user presses Add — a misread amount
 * or date must not land in the books unreviewed. The date deliberately starts
 * empty when the message named only a month, so a bare "for February" cannot
 * silently become the 1st.
 */
const ExpenseDraftCard: React.FC<{ parsed: ExpenseDraft }> = ({ parsed }) => {
  const toast = useToast();
  const { draft } = parsed;

  const [vendor, setVendor] = useState(draft.vendor ?? '');
  const [amount, setAmount] = useState(draft.amount != null ? String(draft.amount) : '');
  const [currency, setCurrency] = useState(draft.currency || 'USD');
  const [category, setCategory] = useState(draft.category ?? '');
  const [date, setDate] = useState(draft.date ?? '');
  const [due, setDue] = useState(draft.due ?? '');
  const [notes, setNotes] = useState(draft.notes ?? '');
  const [paid, setPaid] = useState(draft.status === 'paid');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<ExpenseDTO | null>(null);
  const [discarded, setDiscarded] = useState(false);

  // Currency options always include whatever was parsed, even if it is not one
  // of the popular codes — otherwise the select would silently rewrite it.
  const currencyOptions = useMemo(() => {
    const codes = [...POPULAR_CURRENCIES];
    if (currency && !codes.includes(currency)) codes.unshift(currency);
    return codes;
  }, [currency]);

  const parsedAmount = parseFloat(amount);
  const amountValid = isFinite(parsedAmount) && parsedAmount > 0;
  const canSave = amountValid && vendor.trim().length > 0 && !!date && !submitting;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload: ExpenseCreatePayload = {
        expense_type: 'manual',
        vendor: vendor.trim(),
        category: category || 'other',
        amount: parsedAmount,
        currency: currency.toUpperCase(),
        date,
        due: !paid && due ? due : undefined,
        status: paid ? 'paid' : 'unpaid',
        notes: notes.trim() || undefined,
      };
      const created = await expensesApi.create(payload);
      setSaved(created);
      toast.success('Expense added');
    } catch (e: unknown) {
      setError(errorMessage(e, 'Failed to save expense'));
    } finally {
      setSubmitting(false);
    }
  };

  if (discarded) return null;

  if (saved) {
    return (
      <div className="ml-11 mt-2 max-w-md rounded-xl border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 px-4 py-3 text-sm">
        <div className="flex items-center gap-2 font-medium text-green-800 dark:text-green-300">
          <Check className="w-4 h-4 shrink-0" />
          Saved {saved.amount.toFixed(2)} {saved.currency} to {saved.vendor || 'your expenses'}
        </div>
        <Link
          to="/expenses"
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 hover:underline"
        >
          View in Expenses <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const fieldClass =
    'w-full rounded-lg border border-[var(--color-border)] dark:border-coral-500/20 bg-white dark:bg-transparent ' +
    'px-2.5 py-1.5 text-sm text-[var(--color-foreground)] dark:text-foreground ' +
    'focus:outline-none focus:border-coral-400';
  const labelClass =
    'block mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)] dark:text-foreground-muted';

  return (
    <div className="ml-11 mt-2 max-w-md rounded-xl border border-[var(--color-border)] dark:border-coral-500/20 bg-background-secondary dark:bg-coral-500/5 p-4">
      <div className="mb-3">
        <div className="text-sm font-medium text-[var(--color-foreground)] dark:text-foreground">
          New expense
        </div>
        <div className="text-xs text-[var(--color-foreground-subtle)] dark:text-foreground-muted">
          Nothing is saved until you press Add expense.
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelClass} htmlFor="draft-vendor">Who you paid</label>
          <input
            id="draft-vendor"
            className={fieldClass}
            value={vendor}
            onChange={e => setVendor(e.target.value)}
            placeholder="e.g. Dropbox"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="draft-amount">Amount</label>
          <input
            id="draft-amount"
            className={fieldClass}
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="120.00"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="draft-currency">Currency</label>
          <select
            id="draft-currency"
            className={fieldClass}
            value={currency}
            onChange={e => setCurrency(e.target.value)}
          >
            {currencyOptions.map(code => <option key={code} value={code}>{code}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="draft-date">Date</label>
          <input
            id="draft-date"
            type="date"
            className={fieldClass}
            value={date}
            min={parsed.date_min}
            max={parsed.date_max}
            onChange={e => setDate(e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="draft-category">Category</label>
          <select
            id="draft-category"
            className={fieldClass}
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">Uncategorized</option>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="col-span-2">
          <span className={labelClass}>Have you paid it?</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPaid(true)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                paid
                  ? 'bg-coral-600 border-coral-600 text-white'
                  : 'border-[var(--color-border)] dark:border-coral-500/20 text-[var(--color-foreground-secondary)] dark:text-foreground-secondary'
              }`}
            >
              Paid
            </button>
            <button
              type="button"
              onClick={() => setPaid(false)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                !paid
                  ? 'bg-coral-600 border-coral-600 text-white'
                  : 'border-[var(--color-border)] dark:border-coral-500/20 text-[var(--color-foreground-secondary)] dark:text-foreground-secondary'
              }`}
            >
              Not yet
            </button>
          </div>
        </div>

        {!paid && (
          <div>
            <label className={labelClass} htmlFor="draft-due">Due date (optional)</label>
            <input
              id="draft-due"
              type="date"
              className={fieldClass}
              value={due}
              onChange={e => setDue(e.target.value)}
            />
          </div>
        )}

        <div className="col-span-2">
          <label className={labelClass} htmlFor="draft-notes">Notes (optional)</label>
          <textarea
            id="draft-notes"
            rows={2}
            className={fieldClass}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What was this for?"
          />
        </div>
      </div>

      {parsed.assumptions.length > 0 && (
        <ul className="mt-3 space-y-0.5 text-xs text-[var(--color-foreground-subtle)] dark:text-foreground-muted">
          {parsed.assumptions.map((note, i) => <li key={i}>{note}</li>)}
        </ul>
      )}

      {!date && (
        <div className="mt-2 text-xs text-[var(--color-foreground-subtle)] dark:text-foreground-muted">
          Pick a date to enable saving.
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setDiscarded(true)}
          disabled={submitting}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-foreground-secondary)] dark:text-foreground-secondary hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
        >
          <X className="w-3.5 h-3.5" /> Discard
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSave}
          className="inline-flex items-center gap-1.5 rounded-lg bg-coral-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-coral-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Add expense
        </button>
      </div>
    </div>
  );
};

export default ExpenseDraftCard;
